import type { AskHelixNumericClaimDomainDto } from './ask-helix-claim-domain';
export type { FigurePeriodBasis } from './figure-period-basis';
import type { AskExecutiveAnswerV1 } from './ask-helix/contracts/ask-executive-answer.v1';
import type { AskHelixClaimRoleDto } from './ask-helix-claim-role';
import type {
  AskHelixContextDto,
  AskHelixContextKindDto,
  AskHelixCoverageItemDto,
  AskHelixSubjectKindDto,
  AskHelixSubjectResolutionDto,
} from './ask-helix-context';
import type { MergeAccountingScopeDimensionV1 } from './integrations/merge-accounting';
import type { GateLedgerV1 } from './gate-ledger';
import type { ObjectiveValueGapAnchor } from './objective-to-lever-decomposer';
import type { SourceClassLabel } from './source-class-label';
import type { AskHelixAnswerVerdictStatusDto } from './ask-helix-answer-verdict-statuses';
import type { GovernedToolOutputRowsBlock } from './ask-helix/contracts/governed-tool-output-envelope';
import {
  ASK_HELIX_TURN_OUTCOMES,
  type AskHelixPendingRequestDto,
  type AskHelixResumeOfferFieldsDto,
  type AskHelixTurnOutcomeDto,
} from './ask-helix/contracts/manager-pending-request';
import { ASK_HELIX_PROVIDER_FAILURES, GOVERNED_LOOKUP_FAILURES } from './ask-helix/contracts/provider-failure';
import type { AskHelixPublicTerminalLedgerDto } from './ask-helix/contracts/public-terminal-ledger';
import type { AskHelixRequestedOutputOutcomeDto } from './ask-helix/contracts/requested-output-outcome';
export * from './ask-helix/contracts/public-terminal-ledger';
export * from './ask-helix/contracts/requested-output-outcome';
export {
  ASK_HELIX_ANSWER_VERDICT_STATUSES,
  type AskHelixAnswerVerdictStatusDto,
} from './ask-helix-answer-verdict-statuses';
export type {
  AskHelixContextDto,
  AskHelixContextKindDto,
  AskHelixCoverageItemDto,
  AskHelixCoverageStatusDto,
  AskHelixDecisionAgentModuleContextDto,
  AskHelixDecisionLedgerContextDto,
  AskHelixExecutionCockpitContextDto,
  AskHelixOrgHealthContextDto,
  AskHelixOrgInsightsContextDto,
  AskHelixPortfolioPlannerContextDto,
  AskHelixRequestedScopeDto,
  AskHelixScenarioLabContextDto,
  AskHelixSubjectBindingDto,
  AskHelixSubjectKindDto,
  AskHelixSurfaceBindingDto,
  AskHelixSubjectResolutionDto,
  AskHelixSubjectResolutionStatusDto,
  AskHelixTenantWorkspaceContextDto,
  AskHelixVcbObjectiveContextDto,
} from './ask-helix-context';

export const ASK_HELIX_CONTEXT_KINDS = Object.freeze([
  'portfolio_planner',
  'execution_cockpit',
  'scenario_lab',
  'org_insights',
  'org_health',
  'vcb_objective',
  'tenant_workspace', 'revenue_goal', 'objective', 'decision_agent_module',
  'decision_ledger',
] as const);

export type AskHelixModeDto = 'default' | 'exploratory';

export const ASK_HELIX_EVIDENCE_KINDS = Object.freeze([
  'portfolio_proof_anchor',
  'portfolio_constraint_bucket',
  'portfolio_initiative',
  'exec_work_item',
  'exec_kpi',
  'scenario_option',
  'org_insights_pattern',
  'org_insights_metric',
  'org_budget_scope',
  'org_health_scorecard',
  'org_health_hotspot',
  'org_health_action',
  'vcb_synthesis_snapshot',
  'vcb_evidence_gap',
  'vcb_control_metric',
  'vcb_ontology_node',
  'vcb_supervision_record',
  'vcb_decision_binding',
  'approved_source_evidence',
  'decision_agent_proof',
  'decision_agent_evidence',
  'governed_capability_evidence',
]);

export type AskHelixEvidenceKindDto =
  | 'portfolio_proof_anchor'
  | 'portfolio_constraint_bucket'
  | 'portfolio_initiative'
  | 'exec_work_item'
  | 'exec_kpi'
  | 'scenario_option'
  | 'org_insights_pattern'
  | 'org_insights_metric'
  | 'org_budget_scope'
  | 'org_health_scorecard'
  | 'org_health_hotspot'
  | 'org_health_action'
  | 'vcb_synthesis_snapshot'
  | 'vcb_evidence_gap'
  | 'vcb_control_metric'
  | 'vcb_ontology_node'
  | 'vcb_supervision_record'
  | 'vcb_decision_binding'
  | 'approved_source_evidence'
  | 'decision_agent_proof'
  | 'decision_agent_evidence'
  | 'governed_capability_evidence';

export type AskHelixEvidenceRefDto = {
  kind: AskHelixEvidenceKindDto;
  id: string;
  claim?: string | null;
  // Closed, typed disclosure label projected from the artifact sourceClass.
  // Fixed vocabulary (never free text); surfaced through egress so modeled
  // evidence is visibly disclosed. Absent/`approved_source` renders as before.
  sourceClassLabel?: SourceClassLabel | null;
  // #3906 — INTERNAL JOIN KEY, never egressed.
  //
  // The id of the decisionViewSpec.proofState proof ref that represents this
  // same governed record. `id` above and the proofState proof refs are minted
  // from different id spaces, so the public-egress aliaser used to give this
  // record a `receipt-token-N` while the very same record got `proof-token-N`
  // through proofState — leaving every numeric cell pointing at an evidence id
  // that did not exist in the published list. Carrying the proof ref id lets
  // egress alias both onto ONE opaque token without rewriting either identity
  // (the raw ids stay load-bearing for the governed-table contract, the proof
  // gate and the cross-turn ledger). Empty/absent when this record has no proof
  // ref — that record then stays honestly unresolved.
  proofRefId?: string | null;
};

export type AskHelixActionDto = {
  label: string;
  tool: string;
  args: Record<string, unknown>;
};

export type AskHelixDecisionViewTypeDto =
  | 'daily_brief'
  | 'strategy_portfolio_status'
  | 'value_creation_decision'
  | 'portfolio_reallocation_decision'
  | 'impact_simulation'
  | 'root_cause_convergence'
  | 'scenario_compare'
  | 'board_prep'
  | 'review_pack';

export type AskHelixDecisionViewLayoutModeDto = 'full' | 'split_focus' | 'compact' | 'drill';

export type AskHelixDecisionViewScopeKindDto =
  | 'enterprise'
  | 'strategy'
  | 'initiative'
  | 'org'
  | 'finance'
  | 'policy'
  | 'blueprint'
  | 'approved_capability'
  | 'decision_agent_module'
  | 'governed_capability';

export type AskHelixDecisionViewScopeDto = {
  scopeKind: AskHelixDecisionViewScopeKindDto;
  scopeId: string;
  label?: string | null;
};

export type AskHelixDecisionViewProofLevelDto =
  | 'observed'
  | 'verified'
  | 'connector-derived'
  | 'public'
  | 'manual-admin'
  | 'fixture'
  | 'synthetic-demo'
  | 'assumption'
  | 'gap';

export type AskHelixDecisionViewProofRefDto = {
  proofRefId: string;
  label: string;
  proofLevel: AskHelixDecisionViewProofLevelDto;
  sourceClass?: string | null;
  // Closed, typed disclosure label projected from `sourceClass`. Carried
  // alongside the (genericized) `sourceClass` string so the public evidence
  // surface can render a fixed "Modeled"/"Industry reference"/etc. badge.
  sourceClassLabel?: SourceClassLabel | null;
  sourceId?: string | null;
  evidenceKind?: AskHelixEvidenceKindDto | string | null;
  asOf?: string | null;
  ttlHours?: number | null;
  claim?: string | null;
};

export type AskHelixProofStateSummaryDto = {
  proofRefs: AskHelixDecisionViewProofRefDto[];
  hasFixtureOrDemoProof: boolean;
  hasStaleProof: boolean;
  hasMissingProof: boolean;
  summary?: string | null;
};

export type AskHelixComponentSpecDto = {
  componentId: string;
  componentType: string;
  props: Record<string, unknown>;
  proofRefs?: string[] | null;
  numericPropEvidence?: Record<string, string[]> | null;
  numericProvenance?: Record<string, AskHelixNumericProvenanceDto[]> | null;
  requiredPermissions?: string[] | null;
  redactionReason?: string | null;
};

export type AskHelixNumericProvenanceDto = {
  formulaId: string;
  formulaVersion: string;
  invocationId: string;
  executionEventId: string;
  evidenceArtifactIds: string[];
  sourceSystemRefs: string[];
  evidenceSchemaVersions: string[];
  reportingCutAsOf?: string | null;
};

export type AskHelixFormulaCitationDto = {
  formulaId: string;
  formulaVersion: string;
  invocationId: string;
  executionEventId: string;
  evidenceArtifactIds: string[];
  ingestJobIds: string[];
  sourceSystemRefs: string[];
  evidenceSchemaVersions: string[];
  reportingCutAsOf?: string | null;
  inputDigest: string;
  outputDigest?: string | null;
};

export type AskHelixScenarioRunClaimClassDto = 'scenario-labeled';

export type AskHelixScenarioRunCaseKeyValuesDto = Record<string, string | number | boolean | null>;

export type AskHelixScenarioRunCaseDto = {
  label: string;
  caseId: string;
  keyValues: AskHelixScenarioRunCaseKeyValuesDto;
  // Closed claim class: scenario-labeled values are never actuals.
  claimClass: AskHelixScenarioRunClaimClassDto;
};

export type AskHelixScenarioRunMetadataDto = {
  engineId: string;
  formulaRef: string;
  branchCount: number;
  cases: AskHelixScenarioRunCaseDto[];
  recommendedCase: string;
  recommendedWhy: string;
  tradeoffAccepted: string;
  verdictLine: string;
  /**
   * Machine selection identifiers (policy ref, cycle-alignment basis, weights,
   * score). These are the audit trail, not the reader's prose — the surface keeps
   * them behind a "show technical basis" toggle and NEVER inline in the verdict or
   * the recommended-case why. Absent when no scored basis was produced.
   */
  technicalBasis?: string;
  /**
   * Digit-free disclosure sentences for HOW this run was parameterized, set by
   * the producer when absent caller inputs were resolved to documented defaults
   * (no-opt-in law: defaults + disclosure beat refusal). Rendered verbatim on
   * the scenario disclosure line. Absent (never empty) when the run used
   * caller-supplied parameters only.
   */
  planningDisclosures?: string[];
};

export type AskHelixActionSideEffectClassDto =
  | 'read_only'
  | 'simulation'
  | 'draft_write'
  | 'governed_write'
  | 'activation'
  | 'external_side_effect';

export type AskHelixActionSpecDto = {
  actionId: string;
  label: string;
  intent: string;
  sideEffectClass: AskHelixActionSideEffectClassDto;
  requiredProofRefs?: string[] | null;
  requiredPermissions?: string[] | null;
  blockedReasons?: string[] | null;
  ledgerBinding?: {
    scopeKind: string;
    scopeId: string;
    decisionType?: string | null;
    decisionFamilyId?: string | null;
  } | null;
};

export type AskHelixViewValidatorPolicyDto = {
  requireProofForFactualComponents: boolean;
  blockActivationOnFixtureOrDemo: boolean;
  blockActivationOnStaleProof: boolean;
  blockActivationOnMissingProof: boolean;
};

export type AskHelixDecisionAnswerFamilyDto =
  | 'direct_governed_read'
  | 'ranked_drivers'
  | 'org_health'
  | 'scenario_what_if'
  | 'composite_operating_levers'
  | 'board_prep'
  | 'blocked';

export type AskHelixDecisionViewAuthorityDto = {
  owner: 'helix';
  payloadSource: 'approved_answer_bindings';
  narrationPayloadTrusted: false;
};

export type AskHelixDecisionViewDataBindingDto = {
  componentId: string;
  bindingRefs: string[];
  evidenceRefIds: string[];
  gapIds: string[];
};

export type AskHelixDecisionViewRenderPolicyDto = {
  unsupportedComponentBehavior: 'blocked_rendering_gap';
  noInternalVocabulary: true;
  noUnboundNumbers: true;
};

export type AskHelixDecisionViewSpecDto = {
  schemaVersion: 'decision_view_spec_v1' | 'ask_helix_decision_view_spec_v1';
  specId: string;
  runId?: string | null;
  // #4008 FWF — CANVAS IDENTITY. `specId` is minted from the capability scope
  // alone, so two different questions from two different modules could mint the
  // same spec (the flagship well: four rows, one specId, one objective, one
  // verdict — see `.codex/reports/4008-fwd/`). These two fields carry the turn's
  // OWN identity: which intent the planner actually selected, and which module
  // DECLARES that intent. They are carried from the turn and never derived from
  // the question, the narration moduleId, or the capability scope. Together with
  // a non-null `runId` they are the cheapest regression detector for the whole
  // class: no two cross-module questions may share (specId, objectiveMetricId).
  askedIntentId?: string | null;
  askedModuleId?: string | null;
  viewType: AskHelixDecisionViewTypeDto;
  answerFamily?: AskHelixDecisionAnswerFamilyDto | null;
  viewAuthority?: AskHelixDecisionViewAuthorityDto | null;
  title: string;
  generatedAt: string;
  layout: {
    mode: AskHelixDecisionViewLayoutModeDto;
    focusedId?: string | null;
    selectedIds?: string[] | null;
  };
  scope: AskHelixDecisionViewScopeDto;
  proofState: AskHelixProofStateSummaryDto;
  metadata?: {
    moduleId?: string | null;
    decisionFamilyId?: string | null;
  } & Record<string, unknown>;
  components: AskHelixComponentSpecDto[];
  dataBindings?: AskHelixDecisionViewDataBindingDto[] | null;
  actions: AskHelixActionSpecDto[];
  gaps: string[];
  assumptions: string[];
  renderPolicy?: AskHelixDecisionViewRenderPolicyDto | null;
  validatorPolicy: AskHelixViewValidatorPolicyDto;
};

export type AskHelixViewValidationStatusDto =
  'accepted' | 'accepted_with_labels' | 'accepted_with_blocks' | 'blocked';

// Render-law posture: the closed enum a component renders WITH when its data
// exists. `certified` renders unlabeled-prominent; every other value is a
// subtle evidence-status disclosure chip (never a warning wall). `gap` is the
// only value that means data is genuinely absent / failed a truth check — the
// component renders a NAMED gap card instead of a value.
export type AskHelixRenderPostureDto =
  | 'certified'
  | 'approved_source'
  | 'modeled'
  | 'pending_owner_validation'
  | 'planning_view'
  | 'pre_receipt'
  | 'gap';

// Machine-readable "what to supply" payload for a NAMED gap card. Rendered only
// when a factual component's data is genuinely absent (posture `gap`). The web
// turns this into an executive-safe "Pending — <what>" card; it never enables a
// write and never surfaces validator/ViewSpec vocabulary.
export type AskHelixComponentGapCardDto = {
  // Executive-safe headline, e.g. "Pending owner validation".
  title: string;
  // One-line executive-safe detail of what is missing.
  detail: string;
  // The exact missing pack artifact key, when the gap is a missing artifact.
  missingArtifactKey?: string | null;
  // The owner whose sign-off is required, when the gap is a pending validation.
  ownerSignoff?: string | null;
  // The overlap receipt id required, when the gap is an unresolved overlap.
  overlapReceipt?: string | null;
  // A short executive-safe "what to supply" hint for the reader.
  supplyHint?: string | null;
};

export type AskHelixViewValidationIssueSeverityDto = 'info' | 'warning' | 'error';

export type AskHelixViewValidationIssueDto = {
  severity: AskHelixViewValidationIssueSeverityDto;
  code: string;
  message: string;
  targetId?: string | null;
};

export type AskHelixViewComponentValidationStatusDto =
  'accepted' | 'blocked' | 'redacted' | 'withheld';

export type AskHelixViewComponentValidationResultDto = {
  componentId: string;
  componentType: string;
  status: AskHelixViewComponentValidationStatusDto;
  reasons: string[];
  // Render-law disclosure: the closed posture this component renders WITH when
  // its data exists (present on accepted factual components). Absent on
  // non-factual components. Never enables a write.
  renderPosture?: AskHelixRenderPostureDto | null;
  // Render-law named gap card: present ONLY on blocked factual components whose
  // data is genuinely absent, so the web renders "what to supply" instead of a
  // generic withheld banner.
  gapCard?: AskHelixComponentGapCardDto | null;
};

export type AskHelixViewActionValidationStatusDto = 'accepted' | 'blocked';

export type AskHelixViewActionValidationResultDto = {
  actionId: string;
  status: AskHelixViewActionValidationStatusDto;
  reasons: string[];
};

export type AskHelixViewValidationResultDto = {
  status: AskHelixViewValidationStatusDto;
  accepted: boolean;
  issues: AskHelixViewValidationIssueDto[];
  componentResults: AskHelixViewComponentValidationResultDto[];
  actionResults: AskHelixViewActionValidationResultDto[];
};

export type AskHelixAnswerVerdictDto = {
  status: AskHelixAnswerVerdictStatusDto;
  renderPosture?: AskHelixRenderPostureDto | null;
  gapCard?: AskHelixComponentGapCardDto | null;
  withheldValues?: AskHelixComponentGapCardDto[] | null;
};

export type AskHelixDecisionDeckSlideKindDto =
  | 'executive_summary'
  | 'waterfall'
  | 'material_lever'
  | 'prioritization'
  | 'risk'
  | 'evidence_appendix';

export type AskHelixDecisionDeckSlideDto = {
  slideId: string;
  slideKind: AskHelixDecisionDeckSlideKindDto;
  title: string;
  leverId?: string | null;
  viewSpec: AskHelixDecisionViewSpecDto;
  validation?: AskHelixViewValidationResultDto | null;
  numericBindingRefs?: string[] | null;
  evidenceRefIds?: string[] | null;
};

export type AskHelixDecisionDeckSpecDto = {
  schemaVersion: 'ask_helix_decision_deck_spec_v1';
  deckId: string;
  viewType: 'value_creation_decision';
  intentClass: 'decision_package';
  title: string;
  generatedAt: string;
  sourceViewSpecId?: string | null;
  slides: AskHelixDecisionDeckSlideDto[];
  proofState: AskHelixProofStateSummaryDto;
  assumptions: string[];
  gaps: string[];
  metadata?: Record<string, unknown> | null;
};

export type AskHelixWorkspaceSessionStatusDto = 'ACTIVE' | 'ARCHIVED';

export type AskHelixConversationContextQualityDto =
  'ready' | 'partial' | 'missing' | 'stale' | 'permission_denied';

export type AskHelixConversationContextFrameDto = {
  schemaVersion: 'ask_helix_conversation_context_frame_v1';
  frameId: string;
  conversationId: string;
  workspaceSessionId?: string | null;
  permissionScopeHash?: string | null;
  updatedAt: string;
  contextQuality: AskHelixConversationContextQualityDto;
  sourceTurnIds: string[];
  activeDecision?: {
    sourceTurnId: string;
    question: {
      sourceTurnId: string;
      contentEgress: 'omitted';
      intentLabel?: string | null;
    };
    answerMode?: string | null;
    decisionFamilyId?: string | null;
    moduleRefs: Array<{
      moduleId: string;
      capabilityId?: string | null;
      label?: string | null;
    }>;
    optionRefs: Array<{
      optionId: string;
      ordinal: number;
      label: string;
      source: 'action' | 'follow_up' | 'decision_view_action' | 'scenario_option' | 'module_result';
    }>;
    numericBindingRefs: Array<{
      bindingId: string;
      placeholder?: string | null;
      evidenceId?: string | null;
      claimDomain?: string | null;
      unit?: string | null;
      valueEgress: 'redacted_bound_number';
    }>;
    evidenceRefs: Array<{
      evidenceId: string;
      evidenceClass?: string | null;
    }>;
    gapRefs: string[];
  } | null;
  recentDecisionTurns: Array<{
    sourceTurnId: string;
    answerMode?: string | null;
    decisionFamilyId?: string | null;
    moduleIds: string[];
    optionIds: string[];
    numericBindingIds: string[];
  }>;
  memoryPolicy: {
    rawTranscriptEgress: 'omitted';
    numericValueEgress: 'redacted_bound_numbers';
    sourceBodyEgress: 'omitted';
    permissionFiltered: boolean;
  };
};

export type AskHelixIntentSourceDto =
  | 'session_start'
  | 'chat_turn'
  | 'canvas_hover'
  | 'canvas_click'
  | 'canvas_focus'
  | 'canvas_drill'
  | 'canvas_drag'
  | 'alert'
  | 'approval'
  | 'memory_correction'
  | 'replay'
  | 'system';

export type AskHelixWorkspaceSessionStateDto = {
  sessionId: string;
  conversationId?: string | null;
  tenantId: string;
  createdByUserId: string;
  status: AskHelixWorkspaceSessionStatusDto;
  scope: AskHelixDecisionViewScopeDto;
  currentViewSpecId?: string | null;
  lastKnownGoodViewSpecId?: string | null;
  focusedId?: string | null;
  selectedIds: string[];
  pendingProposalId?: string | null;
  lastIntentId?: string | null;
  startedAt: string;
  updatedAt: string;
};

export type AskHelixIntentEnvelopeDto = {
  intentId: string;
  sessionId: string;
  conversationId?: string | null;
  actorUserId?: string | null;
  intent: string;
  source: AskHelixIntentSourceDto;
  sourceEventId?: string | null;
  scope: AskHelixDecisionViewScopeDto;
  payload: Record<string, unknown>;
  viewSpecId?: string | null;
  runId?: string | null;
  runHash?: string | null;
  validationStatus?: AskHelixViewValidationStatusDto | null;
  createdAt: string;
};

export type AskHelixRecordIntentRequestDto = {
  intent: string;
  source: AskHelixIntentSourceDto;
  sourceEventId?: string | null;
  payload?: Record<string, unknown> | null;
  viewSpecId?: string | null;
  runId?: string | null;
  runHash?: string | null;
  validationStatus?: AskHelixViewValidationStatusDto | null;
};

export type AskHelixWorkspaceSessionResponseDto = {
  session: AskHelixWorkspaceSessionStateDto;
  recentIntents: AskHelixIntentEnvelopeDto[];
};

export type AskHelixCreateWorkspaceSessionRequestDto = {
  source?: 'session_start' | null;
  title?: string | null;
  context: AskHelixContextDto;
};

export type AskHelixCreateWorkspaceSessionResponseDto = {
  workspaceSessionId: string;
  conversationId: string;
  created: boolean;
  session: AskHelixWorkspaceSessionStateDto;
};

export type AskHelixWorkspaceReplayCursorDto = {
  firstIntentId?: string | null;
  lastIntentId?: string | null;
  intentCount: number;
  currentGovernedViewId?: string | null;
  lastKnownGoodGovernedViewId?: string | null;
  runIds: string[];
  runHashes: string[];
  runtimeRunIds: string[];
  evidenceTraceRecordCount: number;
  validationReplayCount: number;
  validationReplayMismatchCount: number;
};

export type AskHelixWorkspaceReplaySessionDto = Omit<
  AskHelixWorkspaceSessionStateDto,
  'currentViewSpecId' | 'lastKnownGoodViewSpecId'
> & {
  currentGovernedViewId?: string | null;
  lastKnownGoodGovernedViewId?: string | null;
};

export type AskHelixWorkspaceReplayIntentPayloadSummaryDto = {
  payloadEgress: 'projected';
  approvedCorrection: boolean;
  pinnedEvidenceRefCount: number;
  unresolvedGapCount: number;
  redactedFieldCount: number;
};

export type AskHelixWorkspaceReplayIntentDto = Omit<
  AskHelixIntentEnvelopeDto,
  'payload' | 'viewSpecId'
> & {
  payloadSummary: AskHelixWorkspaceReplayIntentPayloadSummaryDto;
  governedViewId?: string | null;
};

export type AskHelixWorkspaceMemoryCorrectionDto = {
  correctionId: string;
  appliesToIntentId?: string | null;
  correctionText: string;
  scope: AskHelixDecisionViewScopeDto;
  approvedByUserId?: string | null;
  createdAt: string;
};

export type AskHelixWorkspacePinnedEvidenceRefDto = {
  sourceIntentId: string;
  evidenceRefId: string;
  label?: string | null;
  claim?: string | null;
};

export type AskHelixWorkspaceUnresolvedGapDto = {
  sourceIntentId: string;
  gap: string;
  severity?: 'info' | 'warning' | 'error' | null;
};

export type AskHelixWorkspaceRunLedgerAnswerModeDto = 'answer' | 'blocked';

export type AskHelixWorkspaceRunLedgerEvidenceRefDto = {
  id: string;
  sourceType: string;
  sourceId: string;
  asOf: string;
  description: string;
};

export type AskHelixWorkspaceRunLedgerPermissionDecisionDto = {
  toolName: string;
  allowed: boolean;
  reason: string;
};

export type AskHelixWorkspaceRunLedgerRedactionDecisionDto = {
  field: string;
  action: 'kept' | 'redacted';
  reason: string;
};

export type AskHelixWorkspaceRunLedgerToolCallDto = {
  id: string;
  toolName: string;
  input: unknown;
  purpose: string;
};

export type AskHelixWorkspaceRunLedgerToolTraceDto = {
  call: AskHelixWorkspaceRunLedgerToolCallDto;
  result?: {
    output: unknown;
    evidenceRefs: AskHelixWorkspaceRunLedgerEvidenceRefDto[];
  } | null;
  blockedReason?: string | null;
};

export type AskHelixWorkspaceRunLedgerProofResultDto = {
  passed: boolean;
  reason: string;
  evidenceRefs: AskHelixWorkspaceRunLedgerEvidenceRefDto[];
  blockedReason?: string | null;
};

export type AskHelixWorkspaceRunLedgerRecordDto = {
  runId: string;
  requestId: string;
  requestHash: string;
  contextPackId: string;
  contextPackVersion: string;
  contextPackHash: string;
  surface: 'ask_helix_workspace' | (string & {});
  allowedTools: string[];
  blockedTools: string[];
  permissionDecisions: AskHelixWorkspaceRunLedgerPermissionDecisionDto[];
  redactionDecisions: AskHelixWorkspaceRunLedgerRedactionDecisionDto[];
  toolCalls: AskHelixWorkspaceRunLedgerToolCallDto[];
  toolResults: AskHelixWorkspaceRunLedgerToolTraceDto[];
  evidenceRefs: AskHelixWorkspaceRunLedgerEvidenceRefDto[];
  proofResults: AskHelixWorkspaceRunLedgerProofResultDto[];
  gateLedger?: GateLedgerV1 | null;
  provider?: string | null;
  model?: string | null;
  modelVersion?: string | null;
  outputSchemaName: string;
  outputSchemaVersion: string;
  answerMode: AskHelixWorkspaceRunLedgerAnswerModeDto;
  blockedReason?: string | null;
  evalCaseIds: string[];
  runStartedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
};

export type AskHelixWorkspaceReplayValidationStatusDto =
  | 'matched'
  | 'mismatched'
  | 'missing_snapshot'
  | 'invalid_snapshot'
  | 'permission_recheck_required';

export type AskHelixWorkspaceReplayValidationDto = {
  runId: string;
  requestId: string;
  sourceIntentId?: string | null;
  governedViewId?: string | null;
  viewType?: AskHelixDecisionViewTypeDto | (string & {}) | null;
  status: AskHelixWorkspaceReplayValidationStatusDto;
  persistedValidationStatus?: AskHelixViewValidationStatusDto | null;
  replayedValidationStatus?: AskHelixViewValidationStatusDto | null;
  validationAt?: string | null;
  actorPermissionCount: number;
  issues: string[];
};

export type AskHelixWorkspaceRunLedgerLatencyRollupDto = {
  sampleCount: number;
  minMs?: number | null;
  maxMs?: number | null;
  avgMs?: number | null;
  p50Ms?: number | null;
  p95Ms?: number | null;
};

export type AskHelixWorkspaceRunLedgerCostRollupDto = {
  recordCountWithCost: number;
  synthesisCalls: number;
  estimatedUsd: number;
};

export type AskHelixWorkspaceRuntimeBudgetStatusDto =
  'within_budget' | 'warning' | 'exceeded' | 'unknown';

export type AskHelixWorkspaceRuntimeBudgetMeasureDto = {
  status: AskHelixWorkspaceRuntimeBudgetStatusDto;
  value?: number | null;
  warningThreshold?: number | null;
  hardLimit?: number | null;
};

export type AskHelixWorkspaceRuntimeBudgetAssessmentDto = {
  viewType: AskHelixDecisionViewTypeDto | (string & {});
  status: AskHelixWorkspaceRuntimeBudgetStatusDto;
  latency: AskHelixWorkspaceRuntimeBudgetMeasureDto;
  llmCalls: AskHelixWorkspaceRuntimeBudgetMeasureDto;
  deterministicToolCalls: AskHelixWorkspaceRuntimeBudgetMeasureDto;
  fallbackBehavior?: string | null;
  reasons: string[];
};

export type AskHelixWorkspaceRuntimeBudgetViewTypeRollupDto = {
  viewType: AskHelixDecisionViewTypeDto | (string & {});
  recordCount: number;
  warningCount: number;
  exceededCount: number;
  unknownCount: number;
  p95LatencyMs?: number | null;
  synthesisCalls: number;
  deterministicToolCalls: number;
};

export type AskHelixWorkspaceRuntimeBudgetRollupDto = {
  recordCount: number;
  assessedCount: number;
  status: AskHelixWorkspaceRuntimeBudgetStatusDto;
  warningCount: number;
  exceededCount: number;
  unknownCount: number;
  reasons: string[];
  byViewType: AskHelixWorkspaceRuntimeBudgetViewTypeRollupDto[];
};

export type AskHelixWorkspaceRunLedgerRollupDto = {
  recordCount: number;
  answerCount: number;
  blockedCount: number;
  missingCompletionCount: number;
  cost: AskHelixWorkspaceRunLedgerCostRollupDto;
  latency: AskHelixWorkspaceRunLedgerLatencyRollupDto;
  budget: AskHelixWorkspaceRuntimeBudgetRollupDto;
  validationReplayCount: number;
  validationReplayMismatchCount: number;
};

export type AskHelixWorkspaceReplayTraceDto = {
  session: AskHelixWorkspaceReplaySessionDto;
  replayedAt: string;
  cursor: AskHelixWorkspaceReplayCursorDto;
  intents: AskHelixWorkspaceReplayIntentDto[];
  evidenceTraceRecords: AskHelixWorkspaceRunLedgerRecordDto[];
  validationReplays: AskHelixWorkspaceReplayValidationDto[];
  evidenceTraceRollup: AskHelixWorkspaceRunLedgerRollupDto;
  approvedCorrections: AskHelixWorkspaceMemoryCorrectionDto[];
  pinnedEvidenceRefs: AskHelixWorkspacePinnedEvidenceRefDto[];
  unresolvedGaps: AskHelixWorkspaceUnresolvedGapDto[];
  safeguards: string[];
};

export type AskHelixAnswerStalenessRequestDto = {
  answer: {
    answerId?: string | null;
    generatedAt?: string | null;
  };
};

export type AskHelixAnswerStalenessStatusDto = 'current' | 'stale' | 'unknown';

export type AskHelixAnswerStalenessReasonCodeDto =
  | 'answer_authority_denied'
  | 'evidence_authority_denied'
  | 'input_limit_exceeded'
  | 'evidence_ref_limit_exceeded'
  | 'evidence_lineage_unproven'
  | 'latest_artifact_unavailable';

export type AskHelixAnswerStalenessFamilyDto = {
  evidenceFamily: string;
  evidenceRefId?: string | null;
  answerAsOf: string;
  latestArtifactId?: string | null;
  latestArtifactAsOf?: string | null;
  isNewer: boolean;
};

export type AskHelixAnswerStalenessResponseDto = {
  schemaVersion: 'ask_helix_answer_staleness_v1';
  status: AskHelixAnswerStalenessStatusDto;
  hasNewerEvidence: boolean;
  checkedAt: string;
  answer: {
    answerId?: string | null;
    generatedAt?: string | null;
  };
  reasonCode?: AskHelixAnswerStalenessReasonCodeDto | null;
  families: AskHelixAnswerStalenessFamilyDto[];
};

export type AskHelixRenderWorkspaceIntentRequestDto = {
  intent: string;
  source: AskHelixIntentSourceDto;
  sourceEventId?: string | null;
  payload?: Record<string, unknown> | null;
  context: AskHelixContextDto;
};

export type AskHelixRenderWorkspaceIntentResponseDto = {
  session: AskHelixWorkspaceSessionStateDto;
  intent: AskHelixIntentEnvelopeDto;
  decisionViewSpec: AskHelixDecisionViewSpecDto;
  viewValidation: AskHelixViewValidationResultDto;
};

export type AskHelixWorkspaceAdmissionDecisionDto = {
  reference: string;
  digest: string;
  admittedComponentIds: string[];
  evidenceRefIds: string[];
};

export type AskHelixWorkspaceRuntimeResponseDto = {
  schemaVersion: 'ask_helix_workspace_runtime_response_v1';
  producedBy: 'ask_helix_runtime';
  responseType: 'view_spec';
  status: 'accepted' | 'rejected';
  turnId: string;
  intentId: string;
  sessionId: string;
  conversationId?: string | null;
  receivedAt: string;
  scope: AskHelixDecisionViewScopeDto;
  viewSpec: AskHelixDecisionViewSpecDto;
  validationResult: AskHelixViewValidationResultDto;
  admissionDecision: AskHelixWorkspaceAdmissionDecisionDto;
  gateLedger?: GateLedgerV1 | null;
  blockedReason?: string | null;
  session: AskHelixWorkspaceSessionStateDto;
  intent: AskHelixIntentEnvelopeDto;
};

export type AskHelixWorkspaceActionGateStatusDto = 'allowed' | 'blocked';

export type AskHelixWorkspaceActionExecutionStatusDto =
  'not_attempted' | 'blocked' | 'ready_for_executor' | 'succeeded' | 'failed' | 'idempotent_replay';

export type AskHelixWorkspaceActionActivationPlanStatusDto =
  'not_required' | 'blocked' | 'ready_for_executor' | 'executed';

export type AskHelixWorkspaceActionActivationPlanDto = {
  status: AskHelixWorkspaceActionActivationPlanStatusDto;
  executor: 'decision_ledger' | null;
  writeAttempted: boolean;
  ledgerBinding?: AskHelixActionSpecDto['ledgerBinding'];
  confirmationId?: string | null;
  idempotencyKey?: string | null;
  proofRefIds: string[];
  preconditions: string[];
  blockedReasons: string[];
  nextStep: string;
};

export type AskHelixWorkspaceActionRequestDto = {
  actionId: string;
  render: AskHelixRenderWorkspaceIntentRequestDto;
  confirmationId?: string | null;
  idempotencyKey?: string | null;
};

export type AskHelixWorkspaceActionResponseDto = {
  schemaVersion: 'ask_helix_workspace_action_gate_v1';
  producedBy: 'ask_helix_runtime';
  responseType: 'action_gate';
  gateStatus: AskHelixWorkspaceActionGateStatusDto;
  sessionId: string;
  intentId: string;
  actionId: string;
  action: AskHelixActionSpecDto | null;
  actionValidation: AskHelixViewActionValidationResultDto;
  reasons: string[];
  receivedAt: string;
  viewSpec: AskHelixDecisionViewSpecDto;
  validationResult: AskHelixViewValidationResultDto;
  ledgerBinding?: AskHelixActionSpecDto['ledgerBinding'];
  activationPlan: AskHelixWorkspaceActionActivationPlanDto;
  execution: {
    status: AskHelixWorkspaceActionExecutionStatusDto;
    reason?: string | null;
    jobId?: string | null;
    idempotencyKey?: string | null;
    ledgerDecisionId?: string | null;
    decisionPackDraftId?: string | null;
  };
  session: AskHelixWorkspaceSessionStateDto;
  intent: AskHelixIntentEnvelopeDto;
};

export const ASK_HELIX_NUMERIC_BINDING_FORMATS = Object.freeze([
  'int',
  'float',
  'usd',
  'percent',
  'hours',
  'weeks',
  'raw',
]);

export type AskHelixNumericBindingFormatDto =
  'int' | 'float' | 'usd' | 'percent' | 'hours' | 'weeks' | 'raw';

/**
 * Declares the stored scale of a percent-form numeric binding. The display
 * unit supplies the reader-facing suffix only; renderers use this field for
 * any conversion.
 */
export type AskHelixNumericBindingScaleDto = 'fraction' | 'points';

// #5100 UI-1e — the closed claimDomain set lives in ./ask-helix-claim-domain (declared once,
// published from here so consumers import type and runtime check from the same declaration).
export {
  ASK_HELIX_NUMERIC_CLAIM_DOMAINS,
  type AskHelixNumericClaimDomainDto,
} from './ask-helix-claim-domain';

/**
 * ONE governed dimension of a member's placement — a typed row, never prose.
 *
 * `dimension` names the axis in the producing module's own vocabulary
 * (`business_unit`, `location`, …); `label` is the governed row label the module
 * already publishes for that axis, and `id` its governed identifier when it has
 * one. Nothing downstream parses, reformats, or infers any of these — the
 * narration requirement relays them verbatim, so a reader-facing sentence is
 * composed from governed labels rather than from a pre-composed string.
 */
export type AskHelixNumericBindingPlacementDimensionDto = {
  dimension: string;
  label: string;
  id?: string | null;
};

/**
 * WHERE the member behind a numeric binding sits, as typed rows anchored to the
 * member's own identity.
 *
 * `memberKey` is the SAME governed member identity the binding's ranked row
 * carries, which is what keeps placement from becoming a parallel unanchored
 * list: each placement travels attached to the member it describes, and a
 * consumer never has to pair them up positionally. Deliberately
 * dimension-generic — any ranked-readout module can publish its own axes
 * without this shared contract learning one module's domain vocabulary.
 */
export type AskHelixNumericBindingPlacementDto = {
  memberKey: string;
  dimensions: AskHelixNumericBindingPlacementDimensionDto[];
};

export type AskHelixNumericBindingScopeDto = {
  label: string;
  strategyId?: string | null;
  scopeRootNodeId?: string | null;
  planId?: string | null;
  runId?: string | null;
  orgStateId?: string | null;
  entityKind?: AskHelixSubjectKindDto | 'context' | null;
  entityId?: string | null;
  // #4602 — OPTIONAL typed statement of WHERE this scope sits, published by the
  // producing module from governed row labels it already computed (never
  // free-typed, never recomputed downstream, never derived from ids). Ranked
  // readouts answer "which" from `label`; a question that also asks "where"
  // needs this. Carried to the narration REQUIREMENT, which is built from the
  // pre-strip module result; the provider's per-binding projection does not
  // include the scope object, so this costs the prompt nothing extra.
  placement?: AskHelixNumericBindingPlacementDto | null;
};

export const ASK_HELIX_NUMERIC_BINDING_TIMEFRAME_KINDS = Object.freeze([
  'as_of',
  'week',
  'run_snapshot',
  'point_in_time',
  'window',
]);

export type AskHelixNumericBindingTimeframeKindDto =
  'as_of' | 'week' | 'run_snapshot' | 'point_in_time' | 'window';

export type AskHelixNumericBindingTimeframeDto = {
  periodBasis?: import('./figure-period-basis').FigurePeriodBasis;
  kind: AskHelixNumericBindingTimeframeKindDto;
  label: string;
  asOf?: string | null;
  start?: string | null;
  end?: string | null;
};

export type AskHelixNumericBindingSourceDto = {
  label: string;
  surface: AskHelixContextKindDto;
  evidenceKind: AskHelixEvidenceKindDto;
  asOf: string | null;
  sourceRef?: string | null;
  approver?: string | null;
  approvedAt?: string | null;
  // #8331: the receipt owns revision identity; the source line prints only `label`.
  revisions?: readonly { datasetRef: string; revisionId: string }[] | null;
};

export const ASK_HELIX_NUMERIC_BINDING_RECONCILIATION_METHODS = Object.freeze([
  'direct_observation',
  'sum_of_parts',
  'partial_allocation',
  'derived_from_stated_parameter',
  'solver_output',
  'derived_ratio',
  'scaled_by_fraction',
]);

export type AskHelixNumericBindingReconciliationMethodDto =
  | 'direct_observation'
  | 'sum_of_parts'
  | 'partial_allocation'
  // A number computed by deterministic arithmetic from a governed baseline
  // binding and one asker-stated objective parameter (percent or absolute USD).
  // The value is NOT observed from the approved plan; it is a derived,
  // fully-reconciled projection whose formula is recorded in `rule`.
  | 'derived_from_stated_parameter'
  // Package membership, target fill, and residual computed by the governed
  // ObjectivePackageSolver. The public DTO carries the solver's existing
  // receipt shape without relabeling it as a different reconciliation method.
  | 'solver_output'
  // #4779 amendment 18 — A QUOTIENT, RECONCILED AS ONE.
  //
  // A ratio is not a sum, and stating one as `sum_of_parts` is a FALSE receipt:
  // a risk-adjusted ROI of 2.5 published beside components totalling millions
  // is a total its own declared parts contradict, so the anti-fabrication fence
  // rejects it and the figure never renders. The vocabulary carried no
  // non-additive shape a fence could verify — every other derived binding in
  // the tree declares `components: null` and is therefore verified by nothing —
  // so this is the minimal faithful one: the binding declares its NUMERATOR and
  // DENOMINATOR as roled components, and the fence checks that the stated total
  // is their quotient. Reuses the `numerator`/`denominator` vocabulary
  // `ExactRatioOperandsV1` already established for governed ratio receipts.
  //
  // #4779 amendment 19 — VERIFIED AT ITS OWN DECLARED SCALE, NEVER AT A CENT.
  // The stated total must be the LAWFUL PROJECTION of the operands: exactly
  // `round(numerator / denominator)` at the canonical presentation scale below,
  // compared exactly through the shared exact-decimal core. The reconciliation
  // contract owns these precision semantics; a money tolerance never applies to
  // a dimensionless ratio.
  | 'derived_ratio'
  // #5043 review finding 5 — A RELATIVE TARGET IS A PRODUCT.
  //
  // "Improve EBITDA by 12%" states a target of `baseline x 1.12`. No additive
  // kind can prove that: declaring it as `sum_of_parts` is a false receipt, and
  // declaring it with no components leaves it proved by nothing. This receipt
  // declares its BASE and the FRACTION it is scaled by as roled components, and
  // the fence recomputes the product exactly through the shared exact-decimal
  // core - the same discipline `derived_ratio` applies to a quotient.
  | 'scaled_by_fraction';

/**
 * THE CANONICAL PRESENTATION SCALE OF A `derived_ratio` RECEIPT — 4 decimals.
 *
 * #4779 amendment 19. This scale was implicit in the producer's display rule
 * (`Math.round(quotient * 10000) / 10000`) while the fence verified the receipt
 * under the additive branch's CENT tolerance — a money rule borrowed for a
 * dimensionless ratio, which admitted a stated 2.509 against a true 2.5
 * (0.009 "cents" apart; 0.36% of the figure, and at smaller ratios far worse:
 * stated 0.014 against a true 0.005 is a 180% overstatement, accepted).
 *
 * Promoted here as the ONE declared source both sides read:
 *   * the PRODUCER authors the published ratio as
 *     `divideExactDecimalAtScale(numerator, denominator, THIS)` — the display
 *     rule, no longer a float rounding;
 *   * the FENCE (`numericBindingIsEngineBound`'s `derived_ratio` clause)
 *     recomputes that same projection from the receipt's declared operands and
 *     requires EXACT equality.
 *
 * Unit-neutral and part of the reconciliation contract itself — never
 * `roundMoney`, never an epsilon, never a per-module tolerance table.
 */
export const ASK_HELIX_DERIVED_RATIO_PRESENTATION_SCALE = 4;

/** Non-additive receipts declare exactly one component of each required role. */
export type AskHelixNumericBindingReconciliationComponentRoleDto =
  | 'numerator'
  | 'denominator'
  // `scaled_by_fraction` declares exactly one of each: the value being scaled,
  // and the fraction it is scaled by (1.12 for "+12%", 0.92 for "-8%").
  | 'base'
  | 'fraction';

export type AskHelixNumericBindingReconciliationComponentDto = {
  key: string;
  label: string;
  value: number;
  exactValue?: string;
  role?: AskHelixNumericBindingReconciliationComponentRoleDto | null;
};

export type AskHelixNumericBindingResidualDto = {
  label: string;
  value: number;
  exactValue?: string;
  unit: string;
};

/**
 * THE UNIT A `derived_ratio` IS PUBLISHED IN — #5041.
 *
 * A quotient and its displayed figure are the same quantity in two units, and the fence
 * has to prove the one a reader actually sees. Gross margin divides 2.8B by 17.0B, which
 * is 0.1647 as a bare quotient and 16.4706 as the percentage points the answer prints.
 * Proving only the quotient proves a number nobody was shown - it leaves the printed
 * figure unfenced, which is exactly the fabrication surface this fence exists to close.
 *
 * So the receipt DECLARES its presentation, and the fence projects the operands into that
 * unit before comparing. A CLOSED enum rather than a free multiplier: two units are the
 * only ones any governed ratio has ever published, and an open number would let a producer
 * declare whatever factor makes its figure pass.
 *
 * Omission means `fraction` - the semantics every receipt written before this carried.
 */
export type AskHelixDerivedRatioPresentationDto = 'fraction' | 'percentage_points' | 'currency';

/**
 * How each presentation projects the bare quotient into the figure a reader sees.
 *
 * `exponent` is the power of ten the quotient is multiplied by; `scale` is the number of
 * decimals the DISPLAYED figure carries. Both are needed, and for the same reason: a
 * dimensionless ratio and a percent are published to four decimals, while a
 * currency-per-unit quotient - operating expense per head - is published to CENTS, because
 * money does not carry four decimals. Verifying that one at a ratio's scale would have
 * failed a correct figure over hundredths of a cent.
 */
export const ASK_HELIX_DERIVED_RATIO_PRESENTATIONS: Readonly<
  Record<AskHelixDerivedRatioPresentationDto, Readonly<{ exponent: number; scale: number }>>
> = {
  fraction: { exponent: 0, scale: 4 },
  percentage_points: { exponent: 2, scale: 4 },
  currency: { exponent: 0, scale: 2 },
};

export const ASK_HELIX_DEFAULT_DERIVED_RATIO_PRESENTATION: AskHelixDerivedRatioPresentationDto =
  'fraction';

export type AskHelixNumericBindingReconciliationDto = {
  method: AskHelixNumericBindingReconciliationMethodDto;
  rule: string;
  total?: number | null;
  exactTotal?: string;
  components?: AskHelixNumericBindingReconciliationComponentDto[] | null;
  residual?: AskHelixNumericBindingResidualDto | null;
  /**
   * `derived_ratio` only: the unit the published figure is stated in. The fence divides
   * the declared operands so that the DISPLAYED number carries the canonical presentation
   * scale, then compares against the published value. Omitted means `fraction`.
   */
  presentation?: AskHelixDerivedRatioPresentationDto | null;
};

export type AskHelixNumericBindingVariantProvenanceDto = {
  evidence: AskHelixEvidenceRefDto;
  source: AskHelixNumericBindingSourceDto;
  valuePath: string;
};

export type AskHelixNumericBindingVariantDto = {
  frameLabel: string;
  value: number | null;
  formattedValue: string;
  provenance: AskHelixNumericBindingVariantProvenanceDto;
};

/**
 * Structurally identical to Living Canvas `CanvasBindingDelta`. Keeping the
 * endpoint shape exact lets the client hand this transition to the identity-
 * keyed canvas diff/motion layer without client-side numeric reconstruction.
 */
export type AskHelixNumericBindingVariantTransitionDto = {
  bindingId: string;
  from: { value: number | null; formattedValue: string };
  to: { value: number | null; formattedValue: string };
};

// --- SHOW THE MATH: on-glass derivation (#3365) -----------------------------
// An optional, additive companion to the numeric binding that spells out HOW a
// governed number was computed: the formula with the actual values substituted,
// each input's provenance (source + as-of), the current-vs-benchmark comparison
// where a rate is priced to a benchmark, and the base the rate applies to in
// words. All values are governed, pre-formatted display strings authored
// server-side — the renderer never does arithmetic. Absent on bindings that
// carry no derivation (every existing binding renders unchanged). Survives the
// SSE egress fence the same way `variants` does (governed strings, no raw
// digits authored client-side).

export type AskHelixNumericDerivationOperandRole =
  'base' | 'rate' | 'factor' | 'cost' | 'adjustment' | 'result';

export type AskHelixNumericDerivationOperator = '×' | '−' | '+' | '÷';

export type AskHelixNumericDerivationSourceDto = {
  label: string;
  asOf?: string | null;
  sourceClassLabel?: string | null;
};

// Current-vs-benchmark comparison for a rate priced to a benchmark.
export type AskHelixNumericDerivationBenchmarkDto = {
  currentLabel: string;
  currentValue: string;
  benchmarkLabel: string;
  benchmarkValue: string;
  rationale: string;
  source: string;
  asOf?: string | null;
};

export type AskHelixNumericDerivationOperandDto = {
  role: AskHelixNumericDerivationOperandRole;
  label: string;
  // Governed, pre-formatted display value (e.g. "$474.3M", "7%").
  value: string;
  // The operator that PRECEDES this operand in the expression; omitted on the
  // first (base) operand and on the result.
  operator?: AskHelixNumericDerivationOperator | null;
  source?: AskHelixNumericDerivationSourceDto | null;
  // Which base a rate applies to, in words ("applied to total spend, not
  // addressable"). Behind a single constant where a ruling is pending.
  baseClarity?: string | null;
  benchmark?: AskHelixNumericDerivationBenchmarkDto | null;
};

// A benchmark range band with the chosen point, for a rate anchored to a
// benchmark range (e.g. savings rate 5-9%, chosen 7%).
export type AskHelixNumericDerivationBandDto = {
  low: string;
  high: string;
  chosen: string;
};

export type AskHelixNumericDerivationStageKind =
  'base' | 'carve_out' | 'rate' | 'factor' | 'cost' | 'result';

// One stage in the derivation waterfall (build-up bridge). Each stage carries
// the RUNNING governed value AFTER the stage applies, the transform in words,
// and — where relevant — the exclusion logic named (carve_out) or the benchmark
// range band (rate). Pre-formatted governed strings only.
export type AskHelixNumericDerivationStageDto = {
  id: string;
  label: string;
  kind: AskHelixNumericDerivationStageKind;
  // Governed running value AFTER this stage applies (e.g. "$142.3M").
  runningValue: string;
  // The transform in words ("× 7%", "− $2.4M program cost", "carve out
  // non-addressable spend"). Omitted on the base stage.
  transform?: string | null;
  // Exclusion logic named (carve_out) or rationale for this stage.
  note?: string | null;
  // Benchmark range + chosen point (rate stages priced to a benchmark range).
  band?: AskHelixNumericDerivationBandDto | null;
  source?: AskHelixNumericDerivationSourceDto | null;
};

export type AskHelixNumericDerivationDto = {
  // The full formula, rendered with actual values, e.g.
  // "$142.3M addressable × 7% × 70% − $2.4M program cost = $4.6M".
  expression: string;
  resultLabel: string;
  resultValue: string;
  operands: AskHelixNumericDerivationOperandDto[];
  // Derivation waterfall (build-up bridge) — the partner-grade centerpiece.
  // Ordered stages from the gross base down to the net result. Optional; a
  // derivation may carry operands (input cards) without a staged waterfall.
  stages?: AskHelixNumericDerivationStageDto[] | null;
  // One sanity-anchor line tying the result to the client's own demonstrated
  // pace from filings ("≈2.0% of the SG&A line — inside a 2-5%/yr pace").
  sanityAnchor?: string | null;
  // One-line base clarity for the whole formula, when present.
  basisNote?: string | null;
};

// ─────────────────────────────────────────────────────────────────────────────
// RENDER-GATE DEMOLITION (#4449 GSD, founder order 2026-08-04) — show-with-badge.
//
// Render-side fail-closed suppression is retired. A governed figure that clears
// THE SINGLE FENCE (it is bound to an engine/governed computation output — see
// `numericBindingIsEngineBound` in ask-helix.answer-grader.ts) always renders.
// Everything the retired admission stack used to SUPPRESS is now DISCLOSED on
// this badge instead: the reader sees the number AND how well-sourced it is.
//
// Closed set of four. Server-authored — the web never derives a badge, it only
// renders the one the API stamped, through the existing chip patterns.
//
//   approved       ✓  every provenance field present; nothing outstanding.
//   modeled        ~  value is a declared model/partial, not a full measurement.
//   unverified     ⚠  backing is incomplete (no evidence ref, or the parts do
//                     not reconcile to the stated total).
//   source_pending ⏳  source/scope/timeframe metadata is still being attached.
//
// Severity precedence when several apply: unverified > modeled > source_pending.
// ─────────────────────────────────────────────────────────────────────────────
export type AskHelixProvenanceBadgeDto = 'approved' | 'modeled' | 'unverified' | 'source_pending';

/**
 * THE SEVERITY ORDER STATED ABOVE, AS DATA. Weakest-first: a HIGHER number is a
 * worse-sourced badge, so naming the weakest badge in a set is a max over this
 * map. Read THIS, never `BADGE_RANK` (the manager dedupe tie-break): that map
 * ranks `source_pending` below `unverified`, so reusing it reported
 * `source_pending` for a set holding an unverified figure.
 */
export const PROVENANCE_BADGE_SEVERITY: Readonly<Record<AskHelixProvenanceBadgeDto, number>> =
  Object.freeze({
    approved: 0,
    source_pending: 1,
    modeled: 2,
    unverified: 3,
  });

export type AskHelixFigureProvenanceDto = {
  schemaVersion: 'ask_helix_figure_provenance_v1';
  badge: AskHelixProvenanceBadgeDto;
  // Rendered glyph, fixed per badge. Server-authored so every surface agrees.
  glyph: '✓' | '~' | '⚠' | '⏳';
  // Short reader-facing label for the chip ("Source pending").
  label: string;
  // The named shortfalls, verbatim from the retired gate's own failure labels.
  // This is the migration payload: what the gate used to enforce by hiding the
  // number, the badge now states out loud.
  reasons: readonly string[];
};

export type AskHelixNumericBindingDto = {
  // #8352: requested-output clause; legacy SDK-loop producers have no clause.
  outputId?: string;
  placeholder: string;
  evidence: AskHelixEvidenceRefDto;
  valuePath: string;
  /** Transport projection; exactValue preserves the engine decimal for further computation. */
  value?: number;
  exactValue?: string;
  // THE TWO GOVERNED RENDERINGS OF ONE BINDING, both authored by the ONE
  // display authority (`manager-display-value.ts`), never re-formatted by a
  // client. #5041: `displayValue` is the EXACT string ("$77,327,600", "43.08%")
  // the figures card and the bindings table render. #5405 phase 2:
  // `displayValueCompact` is the executive magnitude ("$77.3M", "43.1%") the
  // SENTENCE renders — substituted into `answerMarkdown` for this binding's
  // `{{n:alias}}` token, so `prose figure = compact(table figure)` is checkable
  // on one published row. Either is absent when that authority resolved no
  // string; the client then shows "— (not computed)" for the cell.
  displayValue?: string;
  displayValueCompact?: string;
  format: AskHelixNumericBindingFormatDto;
  claimDomain: AskHelixNumericClaimDomainDto;
  definition: string;
  // Unaltered published metric definition, separate from the scoped display caption.
  governedDefinition?: string | null;
  // #4631 N2(b) — OPTIONAL producer-authored reader-facing label for THIS
  // figure (e.g. a KPI caption). Authored at the API construction boundary
  // from the producer's own typed metadata (declared metric label + the
  // figure's semantic role), never derived downstream from placeholder keys
  // or scope text. View builders treat this field as the label authority;
  // absent, they may fall back only to junk-free generic wording.
  displayLabel?: string | null;
  unit: string;
  scale?: AskHelixNumericBindingScaleDto | null;
  scope: AskHelixNumericBindingScopeDto;
  timeframe: AskHelixNumericBindingTimeframeDto;
  source: AskHelixNumericBindingSourceDto;
  reconciliation: AskHelixNumericBindingReconciliationDto;
  allocationMethod?: string | null;
  isPartial: boolean;
  variants?: AskHelixNumericBindingVariantDto[] | null;
  variantTransition?: AskHelixNumericBindingVariantTransitionDto | null;
  variantDeltaBindingId?: string | null;
  // SHOW THE MATH (#3365): optional spelled-out derivation for this number.
  derivation?: AskHelixNumericDerivationDto | null;
  // #4449 GSD: honest provenance for THIS figure. Stamped by the grader on
  // every published binding. Absent only on envelopes produced before the
  // demolition; consumers treat absent as "no badge stamped", never as a gate.
  provenance?: AskHelixFigureProvenanceDto | null;
  // writer17 follow-up — the claim's own grain (any producer's figure may be
  // published at several named coordinates: a query row's member refs, a
  // scenario's phased period and its own declared parameter). Carried raw,
  // by the same names the producer used, so a client keys or sorts on a
  // coordinate directly and never splits a placeholder or a label string.
  // Populated generically from `RuntimeV3ClaimProposition.grain`
  // (`publish-numeric-bindings.ts`) for every claim that has one; absent
  // means the figure is ungrouped, never that a coordinate was dropped.
  coordinates?: Readonly<Record<string, string>>;
  // writer23, 2026-09-24 — the claim's own role (`RuntimeV3ClaimRecord.role`:
  // lead/row/aggregate/disclosure/assumption/passage), carried raw by the
  // SAME generic pass-through `coordinates` above uses
  // (`publish-numeric-bindings.ts`'s `buildBinding` for a runtime-v3 claim;
  // `result-envelope.ts`'s `toNumericBinding` for a manager-runtime figure).
  // Absent means this binding was minted by a producer that has not threaded
  // a role yet, never that a role was dropped. Never branched on by kind of
  // dataset or port — the ordering and rendering rules read only this field.
  claimRole?: AskHelixClaimRoleDto;
};

export type AskHelixObjectiveVariantFrameDto = {
  frameLabel: string;
  statedForm: 'percent' | 'absolute_usd';
  statedValue: number;
};

export type AskHelixObjectiveVariantComparisonDto = {
  schemaVersion: 'ask_helix_objective_variant_comparison_v1';
  intentFamily: string;
  frames: AskHelixObjectiveVariantFrameDto[];
  pairedBindingIds: string[];
  deltaBindingIds: string[];
  frameTotals: Array<{ frameLabel: string; bindingId: string }>;
  headlineTemplate: string;
};

export type AskHelixToolCallDto = {
  tool: string;
  args: Record<string, unknown>;
};

export type AskHelixToolResultDto = {
  tool: string;
  args: Record<string, unknown>;
  ok: boolean;
  output?: unknown;
  error?: string | null;
};

export type AskHelixTraceDto = {
  toolCalls: AskHelixToolCallDto[];
  toolResults: AskHelixToolResultDto[];
  scenarioSweepPlan?: {
    conductor: 'registry';
    expectations: Array<{
      leverFamilyId: string;
      moduleId: string;
      intentId: string | null;
      status: 'selected' | 'blocked';
      blockedReason: string | null;
    }>;
  };
  validationErrors?: string[];
};

export type AskHelixAnswerLatencyPhaseTimingsDto = {
  plannerClassificationMs?: number;
  compositionMs?: number;
  narrationLlmMs?: number;
  egressRenderPrepMs?: number;
};

export type AskHelixAnswerLatencyDiagnosticsDto = {
  schemaVersion: 'ask_helix_answer_latency_diagnostics_v1';
  totalMs: number;
  phaseTimings: AskHelixAnswerLatencyPhaseTimingsDto;
  startedAt?: string | null;
  completedAt?: string | null;
};

export type AskHelixDiagnosticsDto = {
  answerLatency?: AskHelixAnswerLatencyDiagnosticsDto | null;
  gateLedger?: GateLedgerV1 | null;
  [key: string]: unknown;
};

/** Registry selection only; clients cannot supply tools, arguments, capabilities, targets, or receipts. */
export type AskHelixExplicitSelectionDto = Readonly<{
  moduleId: string;
  intentId: string;
  metricKey?: string;
}>;

export type {
  AskHelixQueryRequestDto,
  AskReasoningRequest,
} from './ask-helix/contracts/query-request';

export type AskHelixConversationStateDto = {
  conversationId: string;
  lastOpenAiResponseId: string | null;
  contextRevisionId: string;
  activeScope: {
    tenantId: string;
    strategyId?: string | null;
    orgScope?: string | null;
    functionScope?: string | null;
    geography?: string | null;
    timePeriod?: string | null;
    scopeRootNodeId?: string | null;
    orgStateId?: string | null;
  };
  priorConstraints: Array<{
    constraintId: string;
    label: string;
    sourceTurnId: string;
  }>;
  resolvedEntities: Array<{
    mention: string;
    entityType: 'person' | 'org_unit' | 'function' | 'lane' | 'carrier' | 'initiative';
    entityId: string;
    confidence: 'high' | 'medium' | 'low';
  }>;
  priorToolResultRefs: Array<{
    resultRefId: string;
    capabilityId: string;
    envelopeId: string;
    reusableForFollowUp: boolean;
  }>;
  pendingClarification?: {
    clarificationId: string;
    question: string;
    blocksCapabilities: string[];
  } | null;
  activeScenario?: {
    scenarioId: string;
    objective: string;
    constraints: string[];
    includedCapabilities: string[];
  } | null;
};

// ─────────────────────────────────────────────────────────────────────────────
// PLAN PAYLOAD CONTRACT (#5036 BACKEND LEAD) — declared in ONE focused module.
//
// #5140 Q6 engineering condition: the `scenario_set` / `decision_package`
// declarations moved out of this 2.6k-line barrel into
// `./ask-helix/contracts/plan-payload`, and are re-exported here so every
// existing `@helix/lib` import keeps working byte-for-byte. The contract text,
// the rules it states and the field shapes live with the module.
// ─────────────────────────────────────────────────────────────────────────────
export * from './ask-helix/contracts/plan-payload';
export * from './ask-helix/contracts/metric-series';
export * from './ask-helix/contracts/ranked-breakdowns';
// A re-export does not put a name in THIS module's scope, and the response DTO
// below mounts both payloads. Type-only, so it adds nothing at runtime.
import type { AskHelixMetricSeriesDto } from './ask-helix/contracts/metric-series';
import type { AskHelixRankedBreakdownDto } from './ask-helix/contracts/ranked-breakdowns';
import type {
  AskHelixAnswerRecommendationDto as PlanPayloadAnswerRecommendationDto,
  AskHelixDecisionPackageDto as PlanPayloadDecisionPackageDto,
  AskHelixScenarioSetDto as PlanPayloadScenarioSetDto,
} from './ask-helix/contracts/plan-payload';

// TYPED READER STATE. The two server-owned, reader-safe closed enums a surface
// reads instead of writing its own sentence about what happened.
//
// #5041: the clean manager path published `narrationBlockReason` as an open
// string and nothing typed at all for the turn itself, so the web printed a
// CLIENT-authored sentence for an empty completion. Under the typed-server-
// reason law the server states the reason; the surface renders it.
// ─────────────────────────────────────────────────────────────────────────────

// #5361 fix-forward P2-5 (coordinator follow-up) — ASK_HELIX_TURN_OUTCOMES /
// AskHelixTurnOutcomeDto moved to the contracts module alongside the DTO
// they pair with (`request_input` <-> `pendingRequests`); re-exported here
// so this file's own internal field references and every existing consumer
// (including index.ts's curated barrel) are unaffected.
export { ASK_HELIX_TURN_OUTCOMES, type AskHelixTurnOutcomeDto };

/** Closed diagnostics for narration and evidence lookup outcomes. */
export const ASK_HELIX_NARRATION_REASON_CODES = [
  'provider_empty_response',
  'provider_incomplete',
  'provider_interrupted',
  'provider_budget_exhausted',
  'provider_refused',
  'provider_status_unavailable',
  'packet_too_large',
  // Classified failures retain their shared public codes.
  ...ASK_HELIX_PROVIDER_FAILURES.map((failure) => failure.code),
  ...Object.values(GOVERNED_LOOKUP_FAILURES),
] as const;

export type AskHelixNarrationReasonCodeDto = (typeof ASK_HELIX_NARRATION_REASON_CODES)[number];

/**
 * Whether the conversation this turn referenced can still carry lineage.
 *
 * A client must clear its conversation lineage ONLY on a server-authored code
 * from this closed set - never on a generic 4xx, which is also what a validation
 * error, a permission error and a throttle look like. `active` is stated by
 * OMISSION: a turn that answered under a usable conversation carries no code at
 * all, so the presence of this field is itself the signal to act on.
 *
 * NAME NOTE: `conversationState` on the response is already taken by the
 * conversation CONTEXT object (`AskHelixConversationStateDto`), so the closed
 * code travels as `conversationStateCode` on both the response and the typed
 * error body.
 */
export const ASK_HELIX_CONVERSATION_STATES = [
  'active',
  'inactive',
  'unknown_conversation',
] as const;

export type AskHelixConversationStateCodeDto = (typeof ASK_HELIX_CONVERSATION_STATES)[number];

/**
 * writer17 follow-up (2b amendment) — ONE VARIANT AXIS OF A SCENARIO'S OWN
 * DECLARED WHAT-IF GRID, on the wire.
 *
 * Mirrors `RuntimeV3ResultEnvelope['scenarioParameters']` (`packages/lib/src/
 * runtime-v3/result-envelope.ts`) field for field -- carried through, never
 * reshaped. `coordinateKey` and `slotKey` are copied VERBATIM from the
 * capability's own declaration (`RuntimeV3GraphParam.coordinateKey`/
 * `.slotKey`): the two names a client matches a published binding's own
 * `coordinates` on (`binding.coordinates[coordinateKey]` for this param's
 * value, `binding.coordinates[slotKey]` for the axis it varies alongside),
 * by plain equality. No question text, no capability name and no string
 * folding decides whether this is present -- it is present exactly when the
 * answer's own envelope(s) published one.
 */
export type AskHelixScenarioParameterDto = {
  ref: string;
  label: string;
  unit: string;
  values: readonly string[];
  coordinateKey: string;
  slotKey?: string;
};

export type AskHelixNarrationVerificationDto = { reason: string; suggestedQuestion: string };
export type AskHelixQueryResponseDto = {
  answerMarkdown: string;
  savedInvestigation?: import('./ask-helix/contracts/saved-investigation').SavedInvestigationReference;
  investigationSave?: import('./ask-helix/contracts/saved-investigation').SavedInvestigationSaveStatus;
  narrationVerifications?: AskHelixNarrationVerificationDto[];
  executiveAnswer?: AskExecutiveAnswerV1;
  answerTemplateMarkdown?: string | null;
  decisionFamilyId?: string | null;
  narrationMode?: 'llm' | 'deterministic_fallback' | 'blocked' | null;
  narrationBlockReason?: string | null;
  // Typed reader state; omit inapplicable values so clients can test presence without nulls.
  turnOutcome?: AskHelixTurnOutcomeDto;
  terminalLedger?: AskHelixPublicTerminalLedgerDto;
  narrationReasonCode?: AskHelixNarrationReasonCodeDto;
  pendingRequests?: AskHelixPendingRequestDto[]; // #5163 R2 — present only when turnOutcome is 'request_input'.
  // Omitted on successful turns; later lifecycle state needs no second vocabulary.
  conversationStateCode?: AskHelixConversationStateCodeDto;
  truncated?: boolean | null;
  interrupted?: boolean | null;
  evidence: AskHelixEvidenceRefDto[];
  assumptions: string[];
  gaps?: string[] | null;
  actions: AskHelixActionDto[];
  followUps: string[];
  trace?: AskHelixTraceDto | null;
  conversationId?: string | null;
  // Server logical turn ID: durable message ID or bounded ephemeral token; clients never supply it.
  executionTurnId?: string | null;
  // Server SDK invocation ID; joins one receipt and is absent until that invocation starts.
  executionRunId?: string | null;
  // Server-issued deployment identity; null means no deployment SHA is configured.
  buildSha?: string | null;
  // #7765 — the runs this turn refused, in order. Each is a stored refusal
  // receipt `explain_run` can open, so a reader can see WHY a turn produced
  // nothing rather than only that it did. Server-owned; never client-supplied.
  refusedRunIds?: string[] | null;
  // Persisted assistant-message ID; workspace audit uses server issuance, never browser canvas IDs.
  answerTurnId?: string | null;
  // I14-14 P0 (#8234) — a turn's diagnostic trace, egress-minimal by
  // construction: a stage from the closed set, the typed refusal reason (or
  // null for a turn that answered), and a digest of the admitted draft —
  // never the draft itself and never a phrase from it.
  interpretation?: Readonly<{
    stage:
      | 'interpretation'
      | 'resolution'
      | 'lift'
      | 'validation'
      | 'execution'
      | 'publication'
      | 'none';
    reason: string | null;
    draftDigest: string | null;
  }> | null;
  numericBindings?: AskHelixNumericBindingDto[] | null;
  labelBindings?: import('./evidence/governed-fact.contract').GovernedLabelBinding[];
  outputs?: AskHelixRequestedOutputOutcomeDto[] | null;
  // Present only when a scenario graph declares a coordinateKey.
  scenarioParameters?: AskHelixScenarioParameterDto[] | null;
  objectiveVariantComparison?: AskHelixObjectiveVariantComparisonDto | null;
  formulaCitations?: AskHelixFormulaCitationDto[] | null;
  valueGapAnchor?: ObjectiveValueGapAnchor | null;
  proofCoverage?: { numericValues: number; unboundNumericValues: number } | null;
  coverage?: AskHelixCoverageItemDto[] | null;
  subjectResolution?: AskHelixSubjectResolutionDto[] | null;
  decisionViewSpec?: AskHelixDecisionViewSpecDto | null;
  decisionDeckSpec?: AskHelixDecisionDeckSpecDto | null;
  viewValidation?: AskHelixViewValidationResultDto | null;
  answerVerdict?: AskHelixAnswerVerdictDto | null;
  workspaceSessionId?: string | null;
  conversationState?: AskHelixConversationStateDto | null;
  metadata?: Record<string, unknown> | null;
  diagnostics?: AskHelixDiagnosticsDto | null;
  // FENCE-DEGRADE (per-number SSE egress): true when the egress fence withheld
  // one or more UNBOUND governed number VALUES from this streamed answer and let
  // the rest of the answer stream. The invariant is unchanged — no unbound value
  // ever egresses — only the fence's blast radius shrank from the whole stream
  // to the individual number. When true, `withheldValues` names each withheld
  // figure so the web renders one calm gap chip per number instead of the value.
  degraded?: boolean | null;
  // Named gap chips for each withheld unbound value. Reuses the render-law gap
  // card shape (posture `gap`); machine-readable so the web renders "Pending —
  // <what>" chips. Never carries a number, a name, or an internal id.
  withheldValues?: AskHelixComponentGapCardDto[] | null;
  // #5036 plan payload contract (frozen): S2 (#5042)'s scenario sets for a
  // standalone scenario answer. Present only when a producer emits it (Law 3).
  scenarioSets?: PlanPayloadScenarioSetDto[];
  // #5140 P1 (frozen): EVERY producer close, each with the identity of the
  // operation that authored it. Top-level, because delivery must not depend on
  // a complete `scenarioSets[]` — a partial answer whose figures were all
  // withheld still owes the reader the advice the producer already decided.
  // A COLLECTION, because two governed operations may each author one and
  // dropping both (the earlier single-field behaviour) suppressed facts on
  // exactly the multi-capability answers this manager exists for. Present only
  // when a producer authored one (Law 3), OMITTED never null; no manager or
  // narrator ever writes, ranks or synthesises an entry.
  recommendations?: PlanPayloadAnswerRecommendationDto[];
  // #5036 plan payload contract (frozen): S3 (#5043)'s obligation-gated
  // solver package. Present only when a producer emits it (Law 3). When
  // present, per-lever scenario sets nest on `members[].scenarioSet` only —
  // `scenarioSets` above is not also populated for the same answer.
  decisionPackage?: PlanPayloadDecisionPackageDto;
  // The governed trend block. Present only when a producer emitted one (Law 3).
  metricSeries?: AskHelixMetricSeriesDto[];
  // #6164 governed leaderboard, same law; server order is the ranking authority.
  rankedBreakdowns?: AskHelixRankedBreakdownDto[];
  // Generic producer-authored detail rows, each linked to a fact in its source envelope.
  rows?: readonly GovernedToolOutputRowsBlock[];
} & AskHelixResumeOfferFieldsDto;

export type AskHelixWorkspaceCanvasRuntimeResponseDto = {
  answerMarkdown: string;
  evidence: AskHelixEvidenceRefDto[];
  assumptions: string[];
  actions: AskHelixActionDto[];
  followUps: string[];
  decisionViewSpec: AskHelixDecisionViewSpecDto;
  viewValidation: AskHelixViewValidationResultDto;
  withheldValues: AskHelixComponentGapCardDto[];
};

export type AskHelixWorkspaceCanvasTurnDto =
  | {
      kind: 'answer';
      turnId: string;
      answerTurnId: string;
      question: string;
      at: string;
      status: 'active' | 'superseded';
      pinned: boolean;
      receipts: string[];
      runtimeResponse: AskHelixWorkspaceCanvasRuntimeResponseDto;
    }
  | {
      kind: 'explain';
      turnId: string;
      answerTurnId: string;
      bindingId: string;
      label: string;
      binding: AskHelixNumericBindingDto;
      at: string;
      status: 'active';
      pinned: boolean;
    }
  // #4051 EXPB1 repair round 3 (finding 7, Conversation Experience Contract v2 E3): a
  // BLOCKED landing is a turn. It carries no accepted view spec, so it can never be an
  // 'answer' member; it carries the reader-words block instead. ADDITIVE — existing
  // payloads are byte-identical, and clients that only understand 'answer' | 'explain'
  // simply skip it exactly as they did before this member existed.
  | {
      kind: 'blocked';
      turnId: string;
      answerTurnId: string;
      question: string;
      blockedReason: string;
      at: string;
      status: 'active' | 'superseded';
      pinned: boolean;
    };

export type AskHelixWorkspaceCanvasResponseDto = {
  schemaVersion: 'ask_helix_workspace_canvas_v1';
  conversationId: string;
  workspaceSessionId: string;
  turns: AskHelixWorkspaceCanvasTurnDto[];
};

export type AskHelixRecordWorkspaceCanvasExplainRequestDto = {
  workspaceSessionId: string;
  answerTurnId: string;
  bindingId: string;
  label: string;
  sourceEventId?: string | null;
};

export type AskHelixToggleWorkspaceCanvasPinRequestDto = {
  workspaceSessionId: string;
  turnId: string;
  sourceEventId?: string | null;
};

/**
 * One authenticated, server-owned audit snapshot for a numeric binding in a persisted
 * Ask Helix assistant turn. The browser receives this only through the protected
 * conversation endpoint; it must not retain the binding as local authorization state.
 */
export type AskHelixWorkspaceWorkingsResponseDto = {
  schemaVersion: 'ask_helix_workspace_workings_v1';
  conversationId: string;
  workspaceSessionId: string;
  answerTurnId: string;
  bindingId: string;
  askedQuestion: string;
  storedAt: string;
  expiresAt: string;
  binding: AskHelixNumericBindingDto;
};

export type AskHelixProofRequestDto = {
  context: AskHelixContextDto;
  evidence: AskHelixEvidenceRefDto[];
};

export type AskHelixPortfolioProofItemDto = {
  kind: 'portfolio_proof_anchor';
  id: string;
  claim: string;
  support: Array<{ type: 'WORK' | 'DOC' | 'CELL' | 'PARAM'; refId: string; contribution: number }>;
  assumptions: Array<{
    key: string;
    value: string;
    acceptedBy: string | null;
    acceptedAt: string | null;
  }>;
};

export type AskHelixPortfolioConstraintBucketProofItemDto = {
  kind: 'portfolio_constraint_bucket';
  id: string;
  capabilityId: string;
  week: string;
  supplyHours: number | null;
  demandHours: number | null;
  shadowPriceUsdPerHour: number | null;
};

export type AskHelixPortfolioInitiativeProofItemDto = {
  kind: 'portfolio_initiative';
  id: string;
  initiativeId: string;
  title: string;
  currency: string | null;
  estimatedDeliverySpendUsdP50: number | null;
  estimatedDeliverySpendUsdP80: number | null;
  actualExpenseUsd: number | null;
  actualExpenseAsOf: string | null;
  actualExpenseExpenseCount: number | null;
  actualExpenseScopeMappingArtifactId: string | null;
  actualExpenseAccountingArtifactId: string | null;
  actualExpenseMatchedRuleIds: string[];
  openWorkItemsTotal: number | null;
  openMissingAssignee: number | null;
};

export type AskHelixExecWorkItemProofItemDto = {
  kind: 'exec_work_item';
  id: string;
  title: string;
  url: string | null;
  daysSinceUpdate?: number | null;
};

export type AskHelixScenarioOptionProofItemDto = {
  kind: 'scenario_option';
  id: string;
  family: string;
  targetAchievement: number | null;
  hcDelta: number | null;
  costDeltaRunRate: string | null;
  disruptionScore: number | null;
  orgChurnIndex: number | null;
  capabilityCoverageDelta: number | null;
};

export type AskHelixOrgInsightsPatternProofItemDto = {
  kind: 'org_insights_pattern';
  id: string;
  runId: string;
  packVersionId: string | null;
  title: string;
  description: string | null;
  matchScore: number | null;
  proposedOrgStateId: string | null;
  tradeoffs: Array<{ type: 'positive' | 'negative'; text: string }>;
};

export type AskHelixOrgInsightsMetricProofItemDto = {
  kind: 'org_insights_metric';
  id: string;
  pillar: 'structure_speed' | 'cost_efficiency' | 'execution_risk';
  label: string;
  rag: 'green' | 'amber' | 'red';
  deltaType: 'positive' | 'negative' | 'neutral';
  asIs: string;
  toBe: string;
  delta: string;
  benchmark: string;
};

export type AskHelixOrgBudgetScopeProofItemDto = {
  kind: 'org_budget_scope';
  id: string;
  orgStateId: string;
  computedAt: string | null;
  stale: boolean | null;
  entityKind: 'leader' | 'person';
  entityId: string;
  label: string | null;
  currency: 'USD';
  totalAnnualCostUsd: number | null;
  positionCount: number | null;
  coveredPositionCount: number | null;
  costCenters: string[];
  accountingAsOf: string | null;
  actualExpenseUsdT90d: number | null;
  actualExpenseCountT90d: number | null;
  actualExpenseWindowStart: string | null;
  actualExpenseWindowEnd: string | null;
  actualExpenseAdmissionDimension: MergeAccountingScopeDimensionV1 | null;
  actualExpenseAdmissionValues: string[];
  actualExpensePrimaryDimension: MergeAccountingScopeDimensionV1 | null;
  actualExpenseDimensions: Array<{
    dimension: MergeAccountingScopeDimensionV1;
    totals: Array<{
      value: string;
      amountUsd: number;
      expenseCount: number;
    }>;
  }>;
  components: Array<{
    positionId: string;
    label: string | null;
    annualCostUsd: number;
  }>;
};

export type AskHelixOrgHealthScorecardProofItemDto = {
  kind: 'org_health_scorecard';
  id: string;
  orgStateId: string;
  computedAt: string | null;
  stale: boolean | null;
  overallHealthScore: number | null;
  spanRiskScore: number | null;
  centralityRiskScore: number | null;
  depthChainRiskScore: number | null;
  totalNodes: number | null;
  positionNodes: number | null;
  personNodes: number | null;
  unitNodes: number | null;
  capabilityNodes: number | null;
  anomalyRate: number | null;
  centralityTopShare: number | null;
  avgSpan: number | null;
  maxDepth: number | null;
  unmanagedCount: number | null;
};

export type AskHelixOrgHealthHotspotProofItemDto = {
  kind: 'org_health_hotspot';
  id: string;
  nodeId: string;
  label: string | null;
  nodeType: string | null;
  reasons: string[];
  spanDirect: number | null;
  spanTotal: number | null;
  centrality: number | null;
  depth: number | null;
  chainLoad: number | null;
};

export type AskHelixOrgHealthActionProofItemDto = {
  kind: 'org_health_action';
  id: string;
  actionId: string;
  title: string;
  description: string | null;
  status: string;
  impact: string;
  ownerUserId: string | null;
  dueDate: string | null;
  orgGraphNodeId: string | null;
  hotspotReason: string | null;
};

export type AskHelixVcbProofItemDto = {
  kind: Extract<AskHelixEvidenceKindDto, `vcb_${string}`>;
  id: string;
  [key: string]: unknown;
};

export type AskHelixDecisionAgentEvidenceProofItemDto = {
  kind: 'decision_agent_evidence';
  id: string;
  moduleId: string;
  evidenceClass: string;
  sourceClass: string;
  sourceId: string;
  asOf: string | null;
  description: string | null;
  confidence: 'high' | 'medium' | 'low';
  gapStatus: 'available' | 'partial' | 'missing' | 'stale';
  proofRefId: string;
  reconciliationRule: string;
};

export type AskHelixProofItemDto =
  | AskHelixPortfolioProofItemDto
  | AskHelixPortfolioConstraintBucketProofItemDto
  | AskHelixPortfolioInitiativeProofItemDto
  | AskHelixExecWorkItemProofItemDto
  | AskHelixScenarioOptionProofItemDto
  | AskHelixOrgInsightsPatternProofItemDto
  | AskHelixOrgInsightsMetricProofItemDto
  | AskHelixOrgBudgetScopeProofItemDto
  | AskHelixOrgHealthScorecardProofItemDto
  | AskHelixOrgHealthHotspotProofItemDto
  | AskHelixOrgHealthActionProofItemDto
  | AskHelixVcbProofItemDto
  | AskHelixDecisionAgentEvidenceProofItemDto;

export type AskHelixProofResponseDto = {
  items: AskHelixProofItemDto[];
};

export type AskHelixConversationStatusDto = 'ACTIVE' | 'ARCHIVED';

export type AskHelixConversationDto = {
  id: string;
  title: string | null;
  contextKind: string;
  contextJson: unknown;
  status: AskHelixConversationStatusDto;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AskHelixMessageRoleDto = 'user' | 'assistant' | 'system';

export type AskHelixMessageDto = {
  id: string;
  role: AskHelixMessageRoleDto;
  content: string;
  narrationReasonCode?: AskHelixNarrationReasonCodeDto;
  evidence: AskHelixEvidenceRefDto[];
  assumptions: string[];
  actions: AskHelixActionDto[];
  followUps: string[];
  trace: unknown | null;
  createdAt: string;
};

export type AskHelixListConversationsResponseDto = {
  items: AskHelixConversationDto[];
};

export type AskHelixGetConversationResponseDto = {
  conversation: AskHelixConversationDto;
  messages: AskHelixMessageDto[];
};

export type AskHelixMemoryArtifactStatusDto = 'DRAFT' | 'APPROVED' | 'ARCHIVED';

export const ASK_HELIX_STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_SCHEMA_VERSION =
  'strategic_execution_capacity_evidence_v1';

export const ASK_HELIX_CARRIER_TRANSPORTATION_COST_DATA_LAKE_CONTEXT_KIND =
  'decision_agent:carrier_transportation_cost:governed_snapshot';

export const ASK_HELIX_CARRIER_TRANSPORTATION_COST_DATA_LAKE_SCHEMA_VERSION =
  'carrier_transportation_cost_governed_snapshot_v1';

export const ASK_HELIX_FINANCE_DATA_LAKE_CONTEXT_KIND =
  'decision_agent:finance:governed_pnl_snapshot';

export const ASK_HELIX_FINANCE_DATA_LAKE_SCHEMA_VERSION = 'finance_pnl_governed_snapshot_v1';

export const ASK_HELIX_PRICING_REALIZATION_DATA_LAKE_CONTEXT_KIND =
  'decision_agent:pricing_realization:governed_snapshot';

export const ASK_HELIX_PRICING_REALIZATION_DATA_LAKE_SCHEMA_VERSION =
  'pricing_realization_governed_snapshot_v1';

export const ASK_HELIX_PRICING_DISCOUNT_POLICY_AUTHORITY_CONTEXT_KIND =
  'decision_agent:pricing_realization:discount_policy_authority_matrix';

export const ASK_HELIX_PRICING_DISCOUNT_POLICY_AUTHORITY_SCHEMA_VERSION =
  'pricing_discount_policy_authority_matrix_v1';

export const ASK_HELIX_PRICING_DISCOUNT_TRANSACTION_CONTEXT_KIND =
  'decision_agent:pricing_realization:discount_transaction_snapshot';

export const ASK_HELIX_PRICING_DISCOUNT_TRANSACTION_SCHEMA_VERSION =
  'pricing_discount_transaction_snapshot_v1';

export const ASK_HELIX_PRICING_DISCOUNT_POPULATION_GAP_CODE =
  'population_leakage_pending_full_transaction_ingest';

export type AskHelixPricingDiscountCoverageStatus =
  'modeled_aggregate_only' | 'observed_population_complete';

export type AskHelixPricingDiscountDataGap = {
  code: typeof ASK_HELIX_PRICING_DISCOUNT_POPULATION_GAP_CODE;
  severity: 'blocking';
  requiredFor: string;
  resolution: string;
};

export type AskHelixStrategicExecutionIngestJobStatusDto =
  'PENDING_REVIEW' | 'REVIEWED' | 'APPROVED' | 'REJECTED' | 'FAILED';

export type AskHelixMemoryArtifactDto = {
  id: string;
  scopeKey?: string | null;
  scopeRootNodeId: string | null;
  contextKind: string | null;
  title: string;
  description: string | null;
  status: AskHelixMemoryArtifactStatusDto;
  content?: unknown;
  contentJson: unknown;
  sourceClass?: string | null;
  sourceRef?: string | null;
  schemaVersion?: string | null;
  ingestJobId?: string | null;
  createdByUserId: string | null;
  reviewedByUserId?: string | null;
  reviewedAt?: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AskHelixListMemoryArtifactsResponseDto = {
  items: AskHelixMemoryArtifactDto[];
};

export type AskHelixCreateMemoryArtifactRequestDto = {
  scopeRootNodeId?: string | null;
  contextKind?: string | null;
  title: string;
  description?: string | null;
  contentJson: unknown;
};

export type AskHelixCreateMemoryArtifactResponseDto = { id: string };

export type AskHelixImportStrategicExecutionCsvRequestDto = {
  scopeRootNodeId?: string | null;
  sourceClass?: 'csv-admin-upload' | null;
  sourceRef?: string | null;
  schemaVersion: typeof ASK_HELIX_STRATEGIC_EXECUTION_CAPACITY_EVIDENCE_SCHEMA_VERSION;
  filename: string;
  csvText: string;
};

export type AskHelixImportStrategicExecutionCsvResponseDto = {
  ingestJobId: string;
  artifactId: string;
  status: Extract<AskHelixStrategicExecutionIngestJobStatusDto, 'PENDING_REVIEW'>;
  rowCount: number;
  issueCounts: { errors: number; warnings: number };
};

export type AskHelixToolJobStatusDto =
  'PENDING_APPROVAL' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'REJECTED' | 'CANCELLED';

export type AskHelixToolJobDto = {
  id: string;
  toolName: string;
  status: AskHelixToolJobStatusDto;
  requiresApproval: boolean;
  createdAt: string;
  updatedAt: string;
  approvedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  resultJson: unknown | null;
  errorMessage: string | null;
};

export type AskHelixCreateToolJobRequestDto = {
  toolName: string;
  toolArgs: Record<string, unknown>;
  context: AskHelixContextDto;
  conversationId?: string | null;
  idempotencyKey?: string | null;
  requiresApproval?: boolean | null;
};

export type AskHelixCreateToolJobResponseDto = {
  jobId: string;
  status: AskHelixToolJobStatusDto;
};

export type AskHelixGetToolJobResponseDto = AskHelixToolJobDto;

export type AskHelixApproveAndRunToolJobResponseDto = {
  job: AskHelixToolJobDto;
};

export type AskHelixPreviewToolRequestDto = {
  toolName: string;
  toolArgs: Record<string, unknown>;
  context: AskHelixContextDto;
};

export type AskHelixWrRerunDiffSummaryDto = {
  executiveProtection: {
    before: boolean | null;
    after: boolean | null;
    titleSubstrings: string[] | null;
  };
  protectedSeats: {
    beforeCount: number | null;
    afterCount: number | null;
    patchMode: 'merge' | 'replace';
  };
  dynamicRules: { beforeCount: number; afterCount: number; patchMode: 'merge' | 'replace' };
};

export type AskHelixWrProtectedSeatCandidateDto = {
  seatId: string;
  title: string;
  occupantName: string | null;
  function: string;
  level: number;
  alreadyProtected: boolean;
};

export type AskHelixPreviewRerunWorkforceReductionResponseDto = {
  schemaVersion: 'ask_helix_wr_rerun_preview_v1';
  toolName: 'rerun_workforce_reduction';
  fromRunId: string;
  strategyId: string | null;
  patchMode: { dynamicRules: 'merge' | 'replace'; protectedSeatIds: 'merge' | 'replace' };
  disablesExecProtection: boolean;
  requiredAck: { disableExecutiveProtection: boolean };
  sanitizedToolArgs: Record<string, unknown>;
  diffSummary: AskHelixWrRerunDiffSummaryDto;
  protectedSeats?: {
    source: 'title_contains';
    titleContains: string[];
    matchedCount: number;
    maxCandidates: number;
    truncated: boolean;
    candidates: AskHelixWrProtectedSeatCandidateDto[];
    suggestedSeatIds: string[];
  } | null;
  validation: { status: 'PASS' | 'WARN' | 'BLOCK'; checks: any[] };
};

export type AskHelixExecRunDiffSummaryDto = {
  stalledDaysThreshold: { before: number | null; after: number };
  lookbackDaysForThroughput: { before: number | null; after: number };
  initiatives: {
    beforeCount: number | null;
    afterCount: number;
    addedKeys: string[];
    removedKeys: string[];
    changedKeys: string[];
  };
};

export type AskHelixPreviewExecutionCockpitRunResponseDto = {
  schemaVersion: 'ask_helix_execution_cockpit_run_preview_v1';
  toolName: 'run';
  strategyId: string;
  baseline: {
    strategyConfigUpdatedAt: string | null;
    strategyConfigDerivedFrom: string | null;
  };
  sanitizedToolArgs: Record<string, unknown>;
  diffSummary: AskHelixExecRunDiffSummaryDto;
  validation: { status: 'PASS' | 'WARN' | 'BLOCK'; checks: any[] };
};

export type AskHelixPreviewAdoptOrgInsightsScenarioResponseDto = {
  schemaVersion: 'ask_helix_org_insights_adopt_preview_v1';
  toolName: 'adopt_org_insights_scenario';
  strategyId: string;
  runId: string;
  patternId: string;
  patternName: string;
  asIsOrgStateId: string;
  toBeOrgStateId: string | null;
  sanitizedToolArgs: Record<string, unknown>;
  validation: { status: 'PASS' | 'WARN' | 'BLOCK'; checks: any[] };
};

export type AskHelixPreviewRecordOrgPatternDecisionResponseDto = {
  schemaVersion: 'ask_helix_org_pattern_decision_preview_v1';
  toolName: 'record_org_pattern_decision';
  runId: string;
  patternId: string;
  previousDecision: 'accept' | 'reject' | null;
  nextDecision: 'accept' | 'reject';
  sanitizedToolArgs: Record<string, unknown>;
  validation: { status: 'PASS' | 'WARN' | 'BLOCK'; checks: any[] };
};

export type AskHelixPreviewGenerateNeuroTruthToBeForRunResponseDto = {
  schemaVersion: 'ask_helix_neurotruth_generate_to_be_preview_v1';
  toolName: 'generate_neurotruth_to_be_for_run';
  runId: string;
  packVersionId: string | null;
  patternCount: number;
  missingToBeCount: number;
  sanitizedToolArgs: Record<string, unknown>;
  validation: { status: 'PASS' | 'WARN' | 'BLOCK'; checks: any[] };
};

export type AskHelixPreviewToolResponseDto =
  | AskHelixPreviewRerunWorkforceReductionResponseDto
  | AskHelixPreviewExecutionCockpitRunResponseDto
  | AskHelixPreviewAdoptOrgInsightsScenarioResponseDto
  | AskHelixPreviewRecordOrgPatternDecisionResponseDto
  | AskHelixPreviewGenerateNeuroTruthToBeForRunResponseDto;

export type AskHelixToolJobMetricsResponseDto = {
  generatedAt: string;
  windowHours: number;
  since: string;
  quotas: {
    maxPerUserPerHour: number | null;
    maxPerTenantPerHour: number | null;
    maxPendingPerTenant: number | null;
  };
  usage: {
    actorCreatedLastHour: number;
    tenantCreatedLastHour: number;
    openJobsNow: number;
  };
  totals: {
    created: number;
    pendingApproval: number;
    running: number;
    succeeded: number;
    failed: number;
    rejected: number;
    cancelled: number;
  };
  byTool: Array<{
    toolName: string;
    total: number;
    pendingApproval: number;
    running: number;
    succeeded: number;
    failed: number;
    rejected: number;
    cancelled: number;
  }>;
};

export type AskHelixRecentToolJobDto = {
  id: string;
  toolName: string;
  status: AskHelixToolJobStatusDto;
  contextKind: AskHelixContextKindDto | null;
  strategyId: string | null;
  runId: string | null;
  sourceRef: string | null;
  requiresApproval: boolean;
  createdAt: string;
  updatedAt: string;
  createdByUserId: string | null;
  errorMessage: string | null;
};

export type AskHelixRecentToolJobsResponseDto = {
  generatedAt: string;
  filters: {
    status: AskHelixToolJobStatusDto | null;
    toolName: string | null;
    contextKind: AskHelixContextKindDto | null;
    onlyErrors: boolean;
    limit: number;
  };
  items: AskHelixRecentToolJobDto[];
};

export type AskHelixForecastTruthStatusDto = 'GREEN' | 'AMBER' | 'RED';

export type AskHelixForecastTruthByStatusRowDto = {
  predictedStatus: AskHelixForecastTruthStatusDto;
  evaluated: number;
  matched: number;
  accuracy: number;
};

export type AskHelixForecastTruthTimelineRowDto = {
  bucketStart: string;
  evaluated: number;
  matched: number;
  accuracy: number;
};

export type AskHelixForecastTruthMismatchDto = {
  strategyId: string | null;
  initiativeKey: string;
  initiativeName: string;
  predictedStatus: AskHelixForecastTruthStatusDto;
  actualStatus: AskHelixForecastTruthStatusDto;
  predictedAt: string;
  resolvedAt: string;
};

export type AskHelixForecastTruthMetricsResponseDto = {
  generatedAt: string;
  strategyId: string | null;
  windowHours: number;
  horizonDays: number;
  graceHours: number;
  bucketHours: number;
  since: string;
  totals: {
    predictionsEvaluated: number;
    predictionsMatched: number;
    accuracy: number;
    pendingPredictions: number;
    unresolvedPredictions: number;
  };
  byStatus: AskHelixForecastTruthByStatusRowDto[];
  timeline: AskHelixForecastTruthTimelineRowDto[];
  latestMismatches: AskHelixForecastTruthMismatchDto[];
};

export type AskHelixOpsHealthStatusDto = 'healthy' | 'warning' | 'critical';

export type AskHelixOpsHealthAlertCodeDto =
  | 'open_jobs'
  | 'failed_ratio'
  | 'forecast_accuracy'
  | 'unresolved_ratio'
  | 'queue_latency'
  | 'run_latency'
  | 'approval_age';

export type AskHelixOpsHealthAlertSeverityDto = 'warning' | 'critical';

export type AskHelixOpsHealthAlertDto = {
  code: AskHelixOpsHealthAlertCodeDto;
  severity: AskHelixOpsHealthAlertSeverityDto;
  message: string;
  observed: number;
  threshold: number;
};

export type AskHelixOpsHealthResponseDto = {
  generatedAt: string;
  status: AskHelixOpsHealthStatusDto;
  strategyId: string | null;
  windows: {
    metricsWindowHours: number;
    forecastWindowHours: number;
    horizonDays: number;
    graceHours: number;
  };
  thresholds: {
    maxOpenJobs: number;
    maxFailedRatio: number;
    minForecastAccuracy: number;
    maxUnresolvedRatio: number;
    minForecastSample: number;
    maxP95QueueWaitMinutes: number;
    maxP95RunMinutes: number;
    maxPendingApprovalMinutes: number;
  };
  snapshots: {
    toolJobs: {
      created: number;
      failed: number;
      openJobsNow: number;
      failedRatio: number;
      p95QueueWaitMinutes: number;
      p95RunMinutes: number;
      oldestPendingApprovalMinutes: number;
    };
    forecastTruth: {
      predictionsEvaluated: number;
      predictionsMatched: number;
      accuracy: number;
      unresolvedPredictions: number;
      unresolvedRatio: number;
    };
  };
  alerts: AskHelixOpsHealthAlertDto[];
};
